import {Component, Signal, computed} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {toSignal, toObservable} from '@angular/core/rxjs-interop';
import {catchError, combineLatest, distinctUntilChanged, EMPTY, expand, filter, map, of, reduce, switchMap, timer} from 'rxjs';
import {VtkService} from '../../../services/vtk.service';
import {VtkJobDetail, VtkJobMetrics, VtkLogEntry, VtkTask, VtkTaskMetrics, VTK_STATUS_LABELS, VTK_STATUS_COLORS} from '../../../services/vtk.types';
import {ContextService} from '../../../services/context.service';
import {Organization} from '../../../types-repository';
import {Airline} from '../../../types-workflow';
import {LogoAreaComponent, BreadCrumbs} from '../../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../../headline-section/headline-section.component';

@Component({
  selector: 'app-vtk-task-log',
  styles: [`
    :host {
      height: calc(100% - 45px);
      display: table;
    }
  `],
  template: `
    <app-logo-area [breadcrumbs]="breadCrumbs()"></app-logo-area>
    <main>
    <app-headline-section [title]="'Task ' + taskId()"></app-headline-section>
    <section class="cl-section cl-section-regular">
    <div class="container"><div class="row"><div class="col-12">

    @if (task(); as task) {
      <p class="cl-p-large">
        {{ jobId() }} — {{ task.tool }} ({{ task.id }})
        <span class="cl-badge cl-badge-sm" [class]="statusColor(task.status)">{{ statusLabel(task.status) }}</span>
      </p>
    }
    <p class="alert cl-alert-info">Auto-refreshes every 10s</p>

    @if (logs().length > 0) {
      <pre>@for (log of logs(); track $index) {{{ log.message.trimEnd() }}
}</pre>
    } @else {
      <p>No log entries.</p>
    }

    </div></div></div>
    </section>
    </main>
  `,
  imports: [LogoAreaComponent, HeadlineSectionComponent]
})
export class VtkTaskLogComponent {
  organization: Signal<Organization>;
  airline: Signal<Airline>;
  poName: Signal<string>;
  poItemId: Signal<string>;
  jobId: Signal<string>;
  taskId: Signal<string>;
  task: Signal<VtkTask | undefined>;
  taskMetrics: Signal<VtkTaskMetrics | undefined>;
  logs: Signal<VtkLogEntry[]>;
  breadCrumbs: Signal<BreadCrumbs>;

  constructor(
    private route: ActivatedRoute,
    private vtkService: VtkService,
    private contextService: ContextService,
  ) {
    this.organization = this.contextService.organization;
    this.airline = this.contextService.airline;
    this.poName = toSignal(this.route.params.pipe(map(p => p['po']), distinctUntilChanged()), {initialValue: ''});
    this.poItemId = toSignal(this.route.params.pipe(map(p => p['poitem']), distinctUntilChanged()), {initialValue: ''});
    this.jobId = toSignal(this.route.params.pipe(map(p => p['job_id']), distinctUntilChanged()), {initialValue: ''});
    this.taskId = toSignal(this.route.params.pipe(map(p => p['task_id']), distinctUntilChanged()), {initialValue: ''});

    const orgUrn$ = this.route.queryParams.pipe(map(p => p['org_urn'] as string), filter(Boolean), distinctUntilChanged());
    const poName$ = this.route.params.pipe(map(p => p['po']), distinctUntilChanged());
    const jobId$ = this.route.params.pipe(map(p => p['job_id']), distinctUntilChanged());
    const taskId$ = this.route.params.pipe(map(p => p['task_id']), distinctUntilChanged());

    // Get task metadata from the job detail
    this.task = toSignal(
      combineLatest([orgUrn$, poName$, jobId$, taskId$]).pipe(
        switchMap(([orgUrn, poName, jobId, taskId]) =>
          this.vtkService.getJob(orgUrn, poName, jobId).pipe(
            map(job => job.tasks.find(t => String(t.id) === String(taskId))),
            catchError(() => of(undefined))
          )
        )
      )
    );

    // Get task-level metrics
    this.taskMetrics = toSignal(
      combineLatest([orgUrn$, poName$, jobId$, taskId$]).pipe(
        switchMap(([orgUrn, poName, jobId, taskId]) =>
          this.vtkService.getJobMetrics(orgUrn, poName, jobId).pipe(
            map(m => m.tasks.find(t => String(t.task_id) === String(taskId))),
            catchError(() => of(undefined))
          )
        )
      )
    );

    // Poll logs every 10s, fetching all pages
    this.logs = toSignal(
      combineLatest([orgUrn$, poName$, jobId$, taskId$]).pipe(
        switchMap(([orgUrn, poName, jobId, taskId]) =>
          timer(0, 10000).pipe(
            switchMap(() => this.fetchAllLogs(orgUrn, poName, jobId, Number(taskId)))
          )
        )
      ),
      {initialValue: []}
    );

    this.breadCrumbs = computed(() => {
      const org_id = this.organization()?.id;
      const airline = this.airline();
      const po = this.poName();
      const poItemId = this.poItemId();
      const jobId = this.jobId();
      if (!org_id || !airline) return [];
      return [
        {display: 'Content Platform', commands: ['/o', org_id]},
        {display: 'Workflows', commands: ['/o', org_id, 'workflows']},
        {display: airline.airline_name, commands: ['/o', org_id, 'workflows', airline.iata_code]},
        {display: po, commands: ['/o', org_id, 'workflows', airline.iata_code, po]},
        {display: poItemId, commands: ['/o', org_id, 'workflows', airline.iata_code, po, poItemId]},
        {display: `Job ${jobId}`, commands: ['/o', org_id, 'workflows', airline.iata_code, po, poItemId, 'vtk-jobs', jobId]},
      ];
    });
  }

  private fetchAllLogs(orgUrn: string, jobBundle: string, jobId: string, taskId: number) {
    const limit = 1000;
    return this.vtkService.getTaskLog(orgUrn, jobBundle, jobId, taskId, 0, limit).pipe(
      expand(res => {
        const fetched = res.offset + res.results.length;
        return fetched < res.count
          ? this.vtkService.getTaskLog(orgUrn, jobBundle, jobId, taskId, fetched, limit)
          : EMPTY;
      }),
      reduce((all, res) => all.concat(res.results), [] as VtkLogEntry[]),
      catchError(() => of([] as VtkLogEntry[]))
    );
  }

  statusLabel(status: number): string { return VTK_STATUS_LABELS[status] ?? 'Unknown'; }
  statusColor(status: number): string { return VTK_STATUS_COLORS[status] ?? 'cl-color-ash'; }
}
